import { Component, Inject, OnInit } from '@angular/core';
import { BaseDialogFormViewDirective } from '@app/shared/base-views/base-dialog-form-view.directive';
import {
    IActiveIdAndNameDto,
    IClientAccountDto,
    IIdAndLabelDto,
    ILotDto, ILotJobNumbersDto,
    LotContractTypeEnumId,
    LotJobStatusEnumId
} from '@classictechsolutions/hubapi-transpiled-enums';
import { ILotMappedItem, LotsLogicService } from '@app/logic/lots';
import { ILotsLogicService } from '@app/logic/lots/interfaces/i.lots.logic.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LotDetailsDialogComponent } from '@app/views/lot/lot-sidebar/lot-details-dialog/lot-details-dialog.component';
import { ToastService } from '@app/core/services/toast/toast.service';
import { BusinessEntitiesLogicService } from '@app/logic/business-entity';
import { HouseAndLandNationalPermissions } from '@app/core/permissions/national.permission';
import { take } from 'rxjs';

@Component({
    selector: 'cb-lot-national',
    templateUrl: './lot-national.component.html',
    styleUrls: ['./lot-national.component.scss'],
    standalone: false
})
export class LotNationalComponent
    extends BaseDialogFormViewDirective<ILotDto, ILotMappedItem, ILotsLogicService>
    implements OnInit
{
    public static readonly MIN_WIDTH = '800px';
    public availableBusinessEntities: IActiveIdAndNameDto[];
    public originalBusinessEntityId: number;
    public originalJobNumber: string;
    public childLotsJobNumbers: IIdAndLabelDto[];
    public hasChildLots = false;
    public childLotCannotChangeBusinessEntity = false;

    constructor(
        public readonly dialogRef: MatDialogRef<LotDetailsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public readonly data: ILotMappedItem,
        @Inject(ToastService) public readonly toastService: ToastService,
        public readonly businessEntityLogic: BusinessEntitiesLogicService,
        private readonly lotsLogicService: LotsLogicService,
        public readonly nationalPermissions: HouseAndLandNationalPermissions
    ) {
        super(dialogRef, toastService);
        this.businessEntityLogic
            .getSkinnyBusinessEntityDetails()
            .pipe(take(1))
            .subscribe((idAndLabelDtos: IActiveIdAndNameDto[]) => {
                this.availableBusinessEntities = idAndLabelDtos;
            });

        this.mappedItem = data;

        this.lotsLogicService
            .getUnits(this.mappedItem.id)
            .pipe(take(1))
            .subscribe(units => {
                if (units.length > 0) {
                    this.hasChildLots = true;
                }

                if (units.some(u => !this.canEditBusinessEntities(u))) {
                    this.childLotCannotChangeBusinessEntity = true;
                }

                this.childLotsJobNumbers = units.map(({ id, jobNumber }) => {
                    return { id, label: jobNumber };
                });
            });
        this.originalBusinessEntityId = this.mappedItem.businessEntityId;
        this.originalJobNumber = this.mappedItem.jobNumber;
    }

    public vendorChanged(value: IClientAccountDto): void {
        this.mappedItem.vendorClientAccountId = value.id;
    }

    public canEdit(): boolean {
        return this.nationalPermissions.canEdit();
    }

    public getChangeLotDisabledReason(): string | null {
        if (!this.mappedItem.vendorClientAccountId) {
            return 'Please select a vendor.';
        }

        return null;
    }

    public isChild(lot: ILotMappedItem): boolean {
        return lot.parentId !== undefined && lot.parentId !== null;
    }

    public updateContractParties(): void {
        if (!this.mappedItem?.id || !this.mappedItem.vendorClientAccountId) {
            this.toastService.saveError('Please select a vendor.');
            return;
        }

        this.saveInProgress = true;

        this.lotsLogicService
            .updateContractParties(this.mappedItem.id, {
                vendorClientAccountId: this.mappedItem.vendorClientAccountId,
                businessEntityId: this.mappedItem.businessEntityId
            })
            .pipe(take(1))
            .subscribe({
                next: (updatedLot: ILotDto) => {
                    this.toastService.saveSuccess();
                    this.dialogRef.close(updatedLot);
                },
                error: () => {
                    this.toastService.saveError('Failed to update contract parties.');
                },
                complete: () => {
                    this.saveInProgress = false;
                }
            });
    }

    public handleBusinessEntityChanged(businessEntityId: number): void {
        if (this.originalBusinessEntityId === businessEntityId) {
            this.mappedItem.jobNumber = this.originalJobNumber;
        } else {
            this.lotsLogicService
                .getNewJobNumberForLotBusinessEntityChange(this.mappedItem.id, businessEntityId)
                .pipe(take(1))
                .subscribe((res: ILotJobNumbersDto) => {
                    this.mappedItem.jobNumber = res.jobNumber;
                    this.childLotsJobNumbers = res.childJobNumbers;
                });
        }
    }

    public canEditBusinessEntities(lot: ILotDto): boolean {
        return (
            this.canEdit() &&
            this.isValidJobStatusForBusinessEntityChange(lot.jobStatus) &&
            !lot.hasBuildProgrammeGenerated
        );
    }

    private isValidJobStatusForBusinessEntityChange(jobStatus: LotJobStatusEnumId): boolean {
        return (
            jobStatus === LotJobStatusEnumId.None ||
            jobStatus === LotJobStatusEnumId.Scheme ||
            jobStatus === LotJobStatusEnumId.Concepts
        );
    }

    public getChangeBusinessEntityDisabledReason(): string {
        if (!this.isValidJobStatusForBusinessEntityChange(this.mappedItem.jobStatus)) {
            return 'Business Entity cannot be changed after Concept Stage';
        }

        if (this.childLotCannotChangeBusinessEntity) {
            return 'Business Entity cannot be changed when a unit has progressed past the Concept Stage.';
        }
        return '';
    }
}
